import React, { createContext, useCallback, useContext, useState } from 'react';
import ReplaceDeviceWizard from './ReplaceDeviceWizard';

/**
 * WHERE AN OPEN REPLACE WIZARD LIVES — high enough on the page that nothing a
 * swap changes can unmount it.
 *
 * The wizard used to be rendered by whoever opened it: a "Report a problem"
 * button inside a device's row, or the pending-replacement banner. But a swap
 * changes exactly what those rows are built from. The row is keyed on the
 * OUTGOING device, and the order's background refresh (every save on the
 * order screen triggers one, see dataChangeBus) drops that device from the
 * order the moment it is released — taking the row, its button and the
 * wizard inside it with it, mid-run. The banner, likewise, returned null once
 * the order had no pending replacements, so completing one unmounted the
 * wizard before it could show Done.
 *
 * OrderDetail wraps its whole render in `ReplaceWizardHost`; openers call
 * `useReplaceWizard().open(...)`, which hands the wizard to the host. Outside
 * a host (a panel used elsewhere, a test) the opener renders it itself,
 * exactly as before.
 */

type WizardProps = React.ComponentProps<typeof ReplaceDeviceWizard>;
/** Everything the wizard takes; `onClose` is optional — the host closes it either way. */
export type ReplaceWizardOpen = Omit<WizardProps, 'onClose'> & { onClose?: () => void };

type Slot = ReplaceWizardOpen & { key: number };

const HostContext = createContext<((p: ReplaceWizardOpen) => void) | null>(null);

let seq = 0;

function renderSlot(slot: Slot | null, close: () => void): React.ReactNode {
  if (!slot) return null;
  const { key, onClose, ...props } = slot;
  return <ReplaceDeviceWizard key={key} {...props} onClose={() => { close(); onClose?.(); }} />;
}

export const ReplaceWizardHost: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [slot, setSlot] = useState<Slot | null>(null);
  const open = useCallback((p: ReplaceWizardOpen) => setSlot({ ...p, key: ++seq }), []);
  return (
    <HostContext.Provider value={open}>
      {children}
      {renderSlot(slot, () => setSlot(null))}
    </HostContext.Provider>
  );
};

/** `open(props)` shows the wizard; `element` must be rendered by the caller (it is null inside a host). */
export function useReplaceWizard(): { open: (p: ReplaceWizardOpen) => void; element: React.ReactNode } {
  const host = useContext(HostContext);
  const [local, setLocal] = useState<Slot | null>(null);
  const openLocal = useCallback((p: ReplaceWizardOpen) => setLocal({ ...p, key: ++seq }), []);
  return {
    open: host || openLocal,
    element: host ? null : renderSlot(local, () => setLocal(null)),
  };
}
